"use client";

import React from 'react';
import { motion } from "framer-motion";

export interface MobileShowcaseBlockProps {
    subtitle?: string;
    title?: string;
    description?: string;
    images?: string[];
    style?: {
        backgroundColor?: string;
        textColor?: string;
        accentColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
    };
}

const MobileMockup = ({ imageSrc, altText, delay, wrapperClassName = "" }: { imageSrc: string; altText: string; delay: number; wrapperClassName?: string }) => (
    <motion.div 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
        className={`w-full flex justify-center ${wrapperClassName}`}
    >
        <div className="relative w-full max-w-[280px] sm:max-w-[320px] aspect-[9/19] group">
            {/* Buttons */}
            <div className="absolute left-[-3px] top-[90px] w-[3px] h-[16px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors group-hover:from-[#e5e5ea] group-hover:to-[#b0b0b5]" />
            <div className="absolute left-[-3px] top-[120px] w-[3px] h-[32px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors group-hover:from-[#e5e5ea] group-hover:to-[#b0b0b5]" />
            <div className="absolute left-[-3px] top-[164px] w-[3px] h-[32px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors group-hover:from-[#e5e5ea] group-hover:to-[#b0b0b5]" />
            <div className="absolute right-[-3px] top-[140px] w-[3px] h-[48px] bg-gradient-to-l from-[#d1d1d6] to-[#a8a8b0] rounded-r-md transition-colors group-hover:from-[#e5e5ea] group-hover:to-[#b0b0b5]" />
            
            {/* Phone Frame Outer (Silver Edge) */}
            <div className="w-full h-full rounded-[48px] bg-gradient-to-br from-[#e5e5ea] via-[#b0b0b5] to-[#e5e5ea] p-[3px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85)] transition-all duration-500 group-hover:shadow-[0_30px_70px_-10px_rgba(197,168,128,0.1),0_25px_60px_-15px_rgba(0,0,0,0.95)]">
                
                {/* Phone Frame Inner (Black Bezel) */}
                <div className="w-full h-full rounded-[45px] bg-black p-[4px] sm:p-[5px] relative overflow-hidden">
                    
                    {/* Top Speaker Grill */}
                    <div className="absolute top-[1px] sm:top-[1.5px] left-1/2 -translate-x-1/2 w-[30px] sm:w-[40px] h-[2px] bg-[#222] rounded-full z-20" />
                    
                    {/* Screen Image */}
                    <div className="w-full h-full rounded-[41px] sm:rounded-[40px] overflow-hidden bg-zinc-900 relative">
                        {imageSrc && (
                            <img 
                                src={imageSrc} 
                                alt={altText} 
                                className="w-full h-full object-cover object-top"
                            />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 pointer-events-none" />
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

export default function MobileShowcaseBlock({
    subtitle = "04.5 / RESPONSIVE TRANSLATION",
    title = "Bespoke Mobile Interface",
    description = "A showcase of the mobile translation. We designed every screen to preserve editorial elegance and micro-interactions on compact viewports, optimizing booking rates.",
    images = [
        "/images/crowntown_mobile_1.png",
        "/images/crowntown_mobile_2.png",
        "/images/crowntown_mobile_3.png",
        "/images/crowntown_mobile_2.png",
        "/images/crowntown_mobile_3.png",
        "/images/crowntown_mobile_1.png"
    ],
    style = {}
}: MobileShowcaseBlockProps) {
    const bg = style.backgroundColor || "#09090A";
    const text = style.textColor || "#FAF7F2";
    const accent = style.accentColor || "#c5a880";

    const s1 = images[0] || "";
    const s2 = images[1] || "";
    const s3 = images[2] || "";
    const s4 = images[3] || s1;
    const s5 = images[4] || s2;
    const s6 = images[5] || s3;

    return (
        <div 
            className="w-full py-24 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden"
            style={{
                backgroundColor: bg,
                color: text,
                paddingTop: style.paddingTop || "6rem",
                paddingBottom: style.paddingBottom || "6rem"
            }}
        >
            {/* Background Ambient Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[140px] pointer-events-none"
                 style={{ backgroundColor: `${accent}08` }} />
            
            <div className="container mx-auto relative z-10">
                {/* Section Header */}
                <div className="max-w-3xl mb-16 md:mb-24">
                    {subtitle && (
                        <span className="text-xs font-mono tracking-widest block mb-2" style={{ color: accent }}>
                            {subtitle}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl md:text-5xl font-light font-cormorant leading-tight">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-400 text-sm mt-3 font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                {/* Staggered Phone Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 items-start">
                    
                    {/* Column 1: Left */}
                    <div className="flex flex-col items-center w-full">
                        <MobileMockup imageSrc={s1} altText="Mobile Screen 1" delay={0} />
                        <MobileMockup imageSrc={s4} altText="Mobile Screen 4" delay={0.1} wrapperClassName="mt-12 md:mt-[224px] lg:mt-[320px]" />
                    </div>

                    {/* Column 2: Middle */}
                    <div className="flex flex-col items-center w-full">
                        <MobileMockup imageSrc={s2} altText="Mobile Screen 2" delay={0.15} />
                        <MobileMockup imageSrc={s5} altText="Mobile Screen 5" delay={0.25} wrapperClassName="mt-12 md:mt-[320px] lg:mt-[448px]" />
                    </div>

                    {/* Column 3: Right */}
                    <div className="flex flex-col items-center w-full md:pt-32 lg:pt-64">
                        <MobileMockup imageSrc={s3} altText="Mobile Screen 3" delay={0.3} />
                        <MobileMockup imageSrc={s6} altText="Mobile Screen 6" delay={0.4} wrapperClassName="mt-12 md:mt-[288px] lg:mt-[320px]" />
                    </div>

                </div>
            </div>
        </div>
    );
}
